import { useState } from 'react';
import { useCreateContact } from '../../hooks/useContacts';
import { useToast } from '../layout/Toast';
import { uid } from '../../lib/utils';

export default function AddClientDialog({ onCreated, onCancel }) {
  const createContact = useCreateContact();
  const { toast } = useToast();
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ name: '', email: '', phone: '', company: '' });

  const set = (field, value) => setForm((prev) => ({ ...prev, [field]: value }));

  const handleSubmit = async () => {
    if (!form.name.trim()) {
      toast('Client name is required', 'error');
      return;
    }
    setSaving(true);
    try {
      const newContact = {
        id: uid(),
        type: 'client',
        name: form.name.trim(),
        email: form.email.trim(),
        phone: form.phone.trim(),
        company: form.company.trim(),
        createdAt: new Date().toISOString(),
      };
      await createContact.mutateAsync(newContact);
      toast('Client created — ' + newContact.name);
      onCreated(newContact);
    } catch (err) {
      toast('Error creating client: ' + err.message, 'error');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60">
      <div className="bg-surface border border-border rounded-xl shadow-xl w-full max-w-sm mx-4 p-5 space-y-4">
        <h2 className="text-lg font-heading text-text-primary">Add New Client</h2>

        <div className="space-y-3">
          <div>
            <label className="text-2xs text-text-dim uppercase mb-1 block">Name *</label>
            <input
              autoFocus
              type="text"
              value={form.name}
              onChange={(e) => set('name', e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleSubmit()}
              placeholder="Client name"
              className="field w-full text-sm"
            />
          </div>
          <div>
            <label className="text-2xs text-text-dim uppercase mb-1 block">Email</label>
            <input
              type="email"
              value={form.email}
              onChange={(e) => set('email', e.target.value)}
              placeholder="client@email.com"
              className="field w-full text-sm"
            />
          </div>
          <div>
            <label className="text-2xs text-text-dim uppercase mb-1 block">Phone</label>
            <input
              type="tel"
              value={form.phone}
              onChange={(e) => set('phone', e.target.value)}
              placeholder="(555) 123-4567"
              className="field w-full text-sm"
            />
          </div>
          <div>
            <label className="text-2xs text-text-dim uppercase mb-1 block">Company</label>
            <input
              type="text"
              value={form.company}
              onChange={(e) => set('company', e.target.value)}
              placeholder="Company name (optional)"
              className="field w-full text-sm"
            />
          </div>
        </div>

        <div className="flex justify-end gap-2 pt-1">
          <button onClick={onCancel} className="btn-ghost px-4 py-2 text-sm">
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={saving || !form.name.trim()}
            className="btn-primary px-5 py-2 text-sm font-medium disabled:opacity-50"
          >
            {saving ? 'Creating...' : 'Add Client'}
          </button>
        </div>
      </div>
    </div>
  );
}
